import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { formatIST } from '@/lib/utils';
import { IndianRupee } from 'lucide-react';
import { validateField } from '@/lib/validators';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { BottomSheet, Card as PhoneCard, PrimaryButton, StatusPill } from '@/components/phone/primitives';
import { INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';

// older entries kept the payer's name in `recorded_by` alongside a `recorded_by_id`
const payerName = (p) => p.recorded_by_name || (p.recorded_by_id ? p.recorded_by : '');

const PAYMENT_MODES = ['Cash', 'Cheque', 'Bank Transfer', 'UPI', 'Credit'];

const PAYMENT_TONE = { fully_paid: 'positive', partially_paid: 'caution' };

const rupees = (n) => `₹${(n || 0).toLocaleString()}`;

/**
 * The same three numbers and the same form, at a thumb's size: the form opens
 * as a sheet at the bottom of the screen, its mode picked from a sheet rather
 * than a dropdown at the top.
 */
function PhonePayment({
  order, canRecordPayment, payOpen, setPayOpen, payAmount, setPayAmount, payMode, setPayMode,
  payRef, setPayRef, onSubmit,
}) {
  const balance = (order.order_value || 0) - (order.total_paid || 0);
  return (
    <PhoneCard title="Payment" testID="payment-phone">
      <dl className="text-[13px]">
        {[['Order value', order.order_value, Colors.text.primary], ['Paid', order.total_paid, Colors.semantic.positive]].map(([label, value, color]) => (
          <div key={label} className="flex justify-between border-b py-1.5" style={{ borderColor: Colors.border.subtle }}>
            <dt style={{ color: Colors.text.secondary }}>{label}</dt>
            <dd className="font-medium tabular-nums" style={{ color }}>{rupees(value)}</dd>
          </div>
        ))}
        <div className="flex items-center justify-between py-2">
          <dt className="text-[14px] font-bold" style={{ color: Colors.text.primary }}>Balance due</dt>
          <dd className="text-[16px] font-bold tabular-nums" style={{ color: balance > 0 ? Colors.semantic.critical : Colors.semantic.positive }}>
            {rupees(balance)}
          </dd>
        </div>
      </dl>
      {!!order.payment_status && (
        <StatusPill label={order.payment_status.replace(/_/g, ' ')} tone={PAYMENT_TONE[order.payment_status] || 'critical'} />
      )}

      {order.payment_history?.length > 0 && (
        <div className="mt-3 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
          <p className="mb-1 text-[10px] font-medium uppercase tracking-[1px]" style={{ color: Colors.text.tertiary }}>History</p>
          {order.payment_history.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 border-b py-2 last:border-b-0" style={{ borderColor: Colors.border.subtle }}>
              <div className="min-w-0">
                <p className="text-[14px] font-semibold tabular-nums" style={{ color: Colors.text.primary }}>{rupees(p.amount)}</p>
                <p className="truncate text-[12px]" style={{ color: Colors.text.secondary }}>{[p.mode, payerName(p)].filter(Boolean).join(' · ')}</p>
              </div>
              <p className="shrink-0 text-[12px]" style={{ color: Colors.text.tertiary }}>{formatIST(p.date, { compact: true })}</p>
            </div>
          ))}
        </div>
      )}

      {canRecordPayment && (
        <>
          <PrimaryButton className="mt-3" onClick={() => setPayOpen(true)} testID="record-payment-btn">
            <IndianRupee className="h-4 w-4" /> Record payment
          </PrimaryButton>
          <BottomSheet open={payOpen} onClose={() => setPayOpen(false)} title="Record payment" subtitle={order.order_id} testID="payment-sheet">
            <div className="space-y-3">
              <div>
                <p className="mb-1.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>Amount</p>
                <input
                  type="number" inputMode="decimal" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                  placeholder="0" className={`${INPUT_CLASS} text-right tabular-nums`} style={inputStyle}
                  data-testid="payment-amount-input" aria-label="Amount"
                />
              </div>
              <div>
                <p className="mb-1.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>Mode</p>
                <SelectField name="payment_mode" label="How was it paid?" value={payMode} options={PAYMENT_MODES} onChange={setPayMode} placeholder="Select mode" />
              </div>
              <div>
                <p className="mb-1.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>Reference no.</p>
                <input
                  value={payRef} onChange={e => setPayRef(e.target.value)} placeholder="Optional"
                  className={INPUT_CLASS} style={inputStyle} data-testid="payment-ref-input" aria-label="Reference number"
                />
              </div>
              <PrimaryButton onClick={onSubmit} testID="submit-payment-btn">Submit payment</PrimaryButton>
            </div>
          </BottomSheet>
        </>
      )}
    </PhoneCard>
  );
}

export default function PaymentSidebar({
  order,
  canRecordPayment,
  payOpen,
  setPayOpen,
  payAmount,
  setPayAmount,
  payMode,
  setPayMode,
  payRef,
  setPayRef,
  recordPayment,
}) {
  const isMobile = useIsMobile();

  const handleSubmitPayment = () => {
    const amountErr = validateField('amount', payAmount);
    if (amountErr) { toast.error(amountErr); return; }
    const modeErr = validateField('mode', payMode);
    if (modeErr) { toast.error(modeErr); return; }
    recordPayment();
  };

  if (isMobile) {
    return (
      <PhonePayment
        order={order} canRecordPayment={canRecordPayment} payOpen={payOpen} setPayOpen={setPayOpen}
        payAmount={payAmount} setPayAmount={setPayAmount} payMode={payMode} setPayMode={setPayMode}
        payRef={payRef} setPayRef={setPayRef} onSubmit={handleSubmitPayment}
      />
    );
  }

  return (
    <Card className="border-slate-100 shadow-card rounded-xl">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
          <IndianRupee className="h-3.5 w-3.5" /> Payment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Order Value</span>
          <span className="font-medium">₹{(order.order_value || 0).toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Total Paid</span>
          <span className="font-medium text-green-600">₹{(order.total_paid || 0).toLocaleString()}</span>
        </div>
        <Separator />
        <div className="flex justify-between text-sm font-semibold">
          <span>Balance Due</span>
          <span className="text-red-600">₹{((order.order_value || 0) - (order.total_paid || 0)).toLocaleString()}</span>
        </div>
        <Badge className={`text-[10px] w-fit ${order.payment_status === 'fully_paid' ? 'bg-green-50 text-green-700' : order.payment_status === 'partially_paid' ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>
          {order.payment_status?.replace('_', ' ').toUpperCase()}
        </Badge>

        {order.payment_history?.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Payment History</p>
            {order.payment_history.map((p) => (
              <div key={p.id} className="flex justify-between text-xs py-1.5 border-b last:border-0">
                <div>
                  <p className="font-medium">₹{p.amount.toLocaleString()}</p>
                  <p className="text-slate-400">{[p.mode, payerName(p)].filter(Boolean).join(' - ')}</p>
                </div>
                <p className="text-slate-400">{formatIST(p.date, { compact: true })}</p>
              </div>
            ))}
          </div>
        )}

        {canRecordPayment && (
          <Dialog open={payOpen} onOpenChange={setPayOpen}>
            <DialogTrigger asChild>
              <Button className="w-full bg-brand-blue hover:bg-brand-blue-dark text-white text-sm mt-2" data-testid="record-payment-btn">
                <IndianRupee className="h-3.5 w-3.5 mr-1" /> Record Payment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Record Payment</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">Amount</label>
                  <Input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder="0" data-testid="payment-amount-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Mode</label>
                  <Select value={payMode} onValueChange={setPayMode}>
                    <SelectTrigger data-testid="payment-mode-select"><SelectValue placeholder="Select mode" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Credit">Credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Reference No.</label>
                  <Input value={payRef} onChange={e => setPayRef(e.target.value)} placeholder="Optional" data-testid="payment-ref-input" />
                </div>
                <Button onClick={handleSubmitPayment} className="w-full bg-brand-blue hover:bg-brand-blue-dark text-white" data-testid="submit-payment-btn">
                  Submit Payment
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}
